import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { HomeIcon, ClipboardListIcon, CalendarIcon, UserIcon, CheckCircleIcon, XCircleIcon, ClockIcon, PlusCircleIcon, EyeIcon, EditIcon, TrashIcon, CrownIcon, BuildingIcon, UploadIcon, AlertCircleIcon } from 'lucide-react';
import { mockListings, mockVisits, currentUser, formatCurrency, licensePlans, getDaysRemaining, isLicenseExpired } from '../utils/mockData';
import LicenseStatus from '../components/LicenseStatus';
const UserDashboardPage = () => {
  const [activeTab, setActiveTab] = useState<'listings' | 'visits' | 'profile' | 'license'>('listings');
  const [companyLogo, setCompanyLogo] = useState(currentUser.companyLogo || '');
  const userListings = mockListings.filter(listing => listing.owner.id === currentUser.id);
  const userListingIds = userListings.map(listing => listing.id);
  const userVisits = mockVisits.filter(visit => userListingIds.includes(visit.listingId));
  const handleVisitStatusChange = (visitId: string, status: 'accepted' | 'rejected') => {
    console.log(`Visit ${visitId} ${status}`);
  };
  const currentPlan = currentUser.license ? licensePlans.find(p => p.id === currentUser.license.licenseId) : null;
  const handleLogoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      const logoUrl = URL.createObjectURL(file);
      setCompanyLogo(logoUrl);
      // In a real app, you'd upload this to your server
    }
  };
  return <div className="bg-gray-50 min-h-screen w-full" data-id="element-1269">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-1270">
        <h1 className="text-3xl font-bold text-gray-900 mb-8" data-id="element-1271">My Dashboard</h1>
        <div className="flex flex-col md:flex-row gap-8" data-id="element-1272">
          <div className="md:w-64 shrink-0" data-id="element-1273">
            <div className="bg-white rounded-lg shadow-md overflow-hidden" data-id="element-1274">
              <div className="p-6 border-b border-gray-200" data-id="element-1275">
                <div className="flex items-center" data-id="element-1276">
                  {currentUser.companyLogo ? <img src={currentUser.companyLogo} alt={currentUser.companyName} className="h-12 w-12 rounded-full object-cover" data-id="element-1277" /> : <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center text-blue-600" data-id="element-1278">
                      <UserIcon className="h-6 w-6" data-id="element-1279" />
                    </div>}
                  <div className="ml-3" data-id="element-1280">
                    <p className="font-medium text-gray-900" data-id="element-1281">
                      {currentUser.companyName || currentUser.name}
                    </p>
                    <p className="text-sm text-gray-500" data-id="element-1282">{currentUser.email}</p>
                  </div>
                </div>
              </div>
              <nav className="p-2" data-id="element-1283">
                <button onClick={() => setActiveTab('listings')} className={`w-full flex items-center px-4 py-3 rounded-md text-left ${activeTab === 'listings' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-1284">
                  <HomeIcon className="h-5 w-5 mr-3" data-id="element-1285" />
                  My Listings
                </button>
                <button onClick={() => setActiveTab('visits')} className={`w-full flex items-center px-4 py-3 rounded-md text-left ${activeTab === 'visits' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-1286">
                  <CalendarIcon className="h-5 w-5 mr-3" data-id="element-1287" />
                  Visit Requests
                  {userVisits.filter(v => v.status === 'pending').length > 0 && <span className="ml-auto bg-blue-600 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center" data-id="element-1288">
                      {userVisits.filter(v => v.status === 'pending').length}
                    </span>}
                </button>
                <button onClick={() => setActiveTab('license')} className={`w-full flex items-center px-4 py-3 rounded-md text-left ${activeTab === 'license' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-1289">
                  <CrownIcon className="h-5 w-5 mr-3" data-id="element-1290" />
                  License & Plans
                </button>
                <button onClick={() => setActiveTab('profile')} className={`w-full flex items-center px-4 py-3 rounded-md text-left ${activeTab === 'profile' ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`} data-id="element-1291">
                  <UserIcon className="h-5 w-5 mr-3" data-id="element-1292" />
                  My Profile
                </button>
              </nav>
              <div className="p-4 mt-2" data-id="element-1293">
                <Link to="/create-listing" className="w-full flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-medium px-4 py-2 rounded-md transition duration-200" data-id="element-1294">
                  <PlusCircleIcon className="h-5 w-5 mr-2" data-id="element-1295" />
                  Add New Listing
                </Link>
              </div>
            </div>
          </div>
          <div className="flex-1" data-id="element-1296">
            {activeTab === 'listings' && <div className="space-y-6" data-id="element-1297">
                <LicenseStatus data-id="element-1298" />
                <div className="bg-white rounded-lg shadow-md p-6" data-id="element-1299">
                  <div className="flex items-center justify-between mb-6" data-id="element-1300">
                    <h2 className="text-xl font-semibold text-gray-900" data-id="element-1301">
                      My Listings
                    </h2>
                    <Link to="/create-listing" className="flex items-center text-sm bg-primary hover:bg-[rgb(var(--color-primary-dark))] text-white font-medium px-3 py-1.5 rounded-md transition duration-200" data-id="element-1302">
                      <PlusCircleIcon className="h-4 w-4 mr-1" data-id="element-1303" />
                      New Listing
                    </Link>
                  </div>
                  {userListings.length === 0 ? <div className="text-center py-12" data-id="element-1304">
                      <ClipboardListIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="element-1305" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-1306">
                        No listings yet
                      </h3>
                      <p className="text-gray-600 mb-4" data-id="element-1307">
                        You haven't created any property listings yet.
                      </p>
                      <Link to="/create-listing" className="inline-block bg-primary hover:bg-[rgb(var(--color-primary-dark))] text-white font-medium px-4 py-2 rounded-md transition duration-200" data-id="element-1308">
                        Create Your First Listing
                      </Link>
                    </div> : <div className="overflow-x-auto" data-id="element-1309">
                      <table className="min-w-full divide-y divide-gray-200" data-id="element-1310">
                        <thead data-id="element-1311">
                          <tr data-id="element-1312">
                            <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1313">
                              Property
                            </th>
                            <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1314">
                              Price
                            </th>
                            <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1315">
                              Type
                            </th>
                            <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1316">
                              Status
                            </th>
                            <th className="px-4 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1317">
                              Visits
                            </th>
                            <th className="px-4 py-3 bg-gray-50 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1318">
                              Actions
                            </th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200" data-id="element-1319">
                          {userListings.map(listing => {
                      const listingVisits = userVisits.filter(v => v.listingId === listing.id);
                      return <tr key={listing.id} className="hover:bg-gray-50" data-id="element-1320">
                                <td className="px-4 py-4 whitespace-nowrap" data-id="element-1321">
                                  <div className="flex items-center" data-id="element-1322">
                                    <div className="h-10 w-10 flex-shrink-0" data-id="element-1323">
                                      <img className="h-10 w-10 rounded-md object-cover" src={listing.images[0]} alt={listing.title} data-id="element-1324" />
                                    </div>
                                    <div className="ml-4" data-id="element-1325">
                                      <div className="text-sm font-medium text-gray-900 truncate max-w-xs" data-id="element-1326">
                                        {listing.title}
                                      </div>
                                      <div className="text-sm text-gray-500 truncate max-w-xs" data-id="element-1327">
                                        {listing.location}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap" data-id="element-1328">
                                  <div className="text-sm font-medium text-gray-900" data-id="element-1329">
                                    {formatCurrency(listing.price)}
                                  </div>
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap" data-id="element-1330">
                                  <span className={`px-2 py-1 text-xs font-medium rounded-full capitalize ${listing.type === 'house' ? 'bg-blue-100 text-blue-800' : 'bg-green-100 text-green-800'}`} data-id="element-1331">
                                    {listing.type}
                                  </span>
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap" data-id="element-1332">
                                  {listing.status === 'approved' && <span className="flex items-center text-xs font-medium text-green-800 bg-green-100 px-2 py-1 rounded-full" data-id="element-1333">
                                      <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-1334" />
                                      Approved
                                    </span>}
                                  {listing.status === 'pending' && <span className="flex items-center text-xs font-medium text-yellow-800 bg-yellow-100 px-2 py-1 rounded-full" data-id="element-1335">
                                      <ClockIcon className="h-3 w-3 mr-1" data-id="element-1336" />
                                      Under Review
                                    </span>}
                                  {listing.status === 'rejected' && <span className="flex items-center text-xs font-medium text-red-800 bg-red-100 px-2 py-1 rounded-full" data-id="element-1337">
                                      <XCircleIcon className="h-3 w-3 mr-1" data-id="element-1338" />
                                      Rejected
                                    </span>}
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap" data-id="element-1339">
                                  <div className="text-sm text-gray-900" data-id="element-1340">
                                    {listingVisits.length} total
                                    {listingVisits.filter(v => v.status === 'pending').length > 0 && <span className="ml-1 text-xs bg-yellow-100 text-yellow-800 px-1.5 rounded-full" data-id="element-1341">
                                        {listingVisits.filter(v => v.status === 'pending').length}{' '}
                                        pending
                                      </span>}
                                  </div>
                                </td>
                                <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-1342">
                                  <div className="flex justify-end space-x-2" data-id="element-1343">
                                    {listing.status === 'approved' && <Link to={`/listing/${listing.id}`} className="text-blue-600 hover:text-blue-900" title="View" data-id="element-1344">
                                        <EyeIcon className="h-5 w-5" data-id="element-1345" />
                                      </Link>}
                                    {listing.status === 'rejected' && <button className="text-yellow-600 hover:text-yellow-900" title="View rejection reason" onClick={() => alert(listing.rejectionReason || 'No reason provided')} data-id="element-1346">
                                        <AlertCircleIcon className="h-5 w-5" data-id="element-1347" />
                                      </button>}
                                    <Link to={`/edit-listing/${listing.id}`} className="text-indigo-600 hover:text-indigo-900" title="Edit" data-id="element-1348">
                                      <EditIcon className="h-5 w-5" data-id="element-1349" />
                                    </Link>
                                    <button className="text-red-600 hover:text-red-900" onClick={() => console.log('Delete listing', listing.id)} title="Delete" data-id="element-1350">
                                      <TrashIcon className="h-5 w-5" data-id="element-1351" />
                                    </button>
                                  </div>
                                </td>
                              </tr>;
                    })}
                        </tbody>
                      </table>
                    </div>}
                </div>
              </div>}
            {activeTab === 'visits' && <div className="bg-white rounded-lg shadow-md p-6" data-id="element-1352">
                <h2 className="text-xl font-semibold text-gray-900 mb-6" data-id="element-1353">
                  Visit Requests
                </h2>
                {userVisits.length === 0 ? <div className="text-center py-12" data-id="element-1354">
                    <CalendarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" data-id="element-1355" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-1356">
                      No visit requests
                    </h3>
                    <p className="text-gray-600" data-id="element-1357">
                      You don't have any visit requests for your properties yet.
                    </p>
                  </div> : <div className="space-y-6" data-id="element-1358">
                    {userVisits.map(visit => {
                const listing = mockListings.find(l => l.id === visit.listingId);
                return <div key={visit.id} className="border border-gray-200 rounded-lg p-4" data-id="element-1359">
                          <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-4" data-id="element-1360">
                            <div className="flex items-center mb-2 md:mb-0" data-id="element-1361">
                              <div className="h-10 w-10 flex-shrink-0" data-id="element-1362">
                                <img className="h-10 w-10 rounded-md object-cover" src={listing?.images[0]} alt={listing?.title} data-id="element-1363" />
                              </div>
                              <div className="ml-4" data-id="element-1364">
                                <div className="text-sm font-medium text-gray-900" data-id="element-1365">
                                  {listing?.title}
                                </div>
                                <div className="text-sm text-gray-500" data-id="element-1366">
                                  {listing?.location}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center" data-id="element-1367">
                              {visit.status === 'pending' && <span className="flex items-center text-yellow-600 bg-yellow-100 px-2 py-1 rounded-full text-xs font-medium" data-id="element-1368">
                                  <ClockIcon className="h-3 w-3 mr-1" data-id="element-1369" />
                                  Pending
                                </span>}
                              {visit.status === 'accepted' && <span className="flex items-center text-green-600 bg-green-100 px-2 py-1 rounded-full text-xs font-medium" data-id="element-1370">
                                  <CheckCircleIcon className="h-3 w-3 mr-1" data-id="element-1371" />
                                  Accepted
                                </span>}
                              {visit.status === 'rejected' && <span className="flex items-center text-red-600 bg-red-100 px-2 py-1 rounded-full text-xs font-medium" data-id="element-1372">
                                  <XCircleIcon className="h-3 w-3 mr-1" data-id="element-1373" />
                                  Declined
                                </span>}
                            </div>
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4" data-id="element-1374">
                            <div data-id="element-1375">
                              <p className="text-xs text-gray-500" data-id="element-1376">Visitor</p>
                              <p className="text-sm font-medium" data-id="element-1377">
                                {visit.userName}
                              </p>
                              <p className="text-sm text-gray-600" data-id="element-1378">
                                {visit.userEmail}
                              </p>
                              <p className="text-sm text-gray-600" data-id="element-1379">
                                {visit.userPhone}
                              </p>
                            </div>
                            <div data-id="element-1380">
                              <p className="text-xs text-gray-500" data-id="element-1381">
                                Date & Time
                              </p>
                              <p className="text-sm font-medium" data-id="element-1382">
                                {visit.date}
                              </p>
                              <p className="text-sm text-gray-600" data-id="element-1383">
                                {visit.time}
                              </p>
                            </div>
                            <div data-id="element-1384">
                              <p className="text-xs text-gray-500" data-id="element-1385">Message</p>
                              <p className="text-sm text-gray-600" data-id="element-1386">
                                {visit.message || 'No message provided'}
                              </p>
                            </div>
                          </div>
                          {visit.status === 'pending' && <div className="flex justify-end space-x-3" data-id="element-1387">
                              <button onClick={() => handleVisitStatusChange(visit.id, 'rejected')} className="px-3 py-1 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-50" data-id="element-1388">
                                Decline
                              </button>
                              <button onClick={() => handleVisitStatusChange(visit.id, 'accepted')} className="px-3 py-1 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700" data-id="element-1389">
                                Accept
                              </button>
                            </div>}
                        </div>;
              })}
                  </div>}
              </div>}
            {activeTab === 'license' && <div className="space-y-6" data-id="element-1390">
                <div className="bg-white rounded-lg shadow-md p-6" data-id="element-1391">
                  <h2 className="text-xl font-semibold text-gray-900 mb-6" data-id="element-1392">
                    License & Subscription
                  </h2>
                  {currentUser.license ? <div className="space-y-6" data-id="element-1393">
                      <div className="bg-gradient-to-r from-blue-50 to-blue-100 rounded-lg p-6" data-id="element-1394">
                        <div className="flex items-start justify-between mb-4" data-id="element-1395">
                          <div data-id="element-1396">
                            <p className="text-sm text-blue-700 mb-1" data-id="element-1397">
                              Current Plan
                            </p>
                            <h3 className="text-2xl font-bold text-blue-900 capitalize" data-id="element-1398">
                              {currentPlan?.planName}
                            </h3>
                          </div>
                          <CrownIcon className="h-8 w-8 text-blue-600" data-id="element-1399" />
                        </div>
                        <div className="grid grid-cols-2 gap-4 mb-4" data-id="element-1400">
                          <div data-id="element-1401">
                            <p className="text-sm text-blue-700" data-id="element-1402">
                              Listings Used
                            </p>
                            <p className="text-xl font-bold text-blue-900" data-id="element-1403">
                              {currentUser.license.usedListings} /{' '}
                              {currentUser.license.maxListings}
                            </p>
                          </div>
                          <div data-id="element-1404">
                            <p className="text-sm text-blue-700" data-id="element-1405">
                              Days Remaining
                            </p>
                            <p className="text-xl font-bold text-blue-900" data-id="element-1406">
                              {getDaysRemaining(currentUser.license.expiryDate)}
                            </p>
                          </div>
                        </div>
                        <div className="w-full bg-blue-200 rounded-full h-3 mb-4" data-id="element-1407">
                          <div className="bg-blue-600 h-3 rounded-full transition-all duration-300" style={{
                      width: `${currentUser.license.usedListings / currentUser.license.maxListings * 100}%`
                    }} data-id="element-1408" />
                        </div>
                        <p className="text-sm text-blue-700 mb-4" data-id="element-1409">
                          Expires on{' '}
                          {new Date(currentUser.license.expiryDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })}
                        </p>
                        <Link to="/license-plans" className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-2 rounded-md transition duration-200" data-id="element-1410">
                          Upgrade Plan
                        </Link>
                      </div>
                      <div data-id="element-1411">
                        <h3 className="text-lg font-semibold text-gray-900 mb-3" data-id="element-1412">
                          Plan Features
                        </h3>
                        <ul className="space-y-2" data-id="element-1413">
                          {currentPlan?.features.map((feature, index) => <li key={index} className="flex items-center text-gray-700" data-id="element-1414">
                              <CheckCircleIcon className="h-5 w-5 text-green-500 mr-2" data-id="element-1415" />
                              {feature}
                            </li>)}
                        </ul>
                      </div>
                    </div> : <div className="text-center py-8" data-id="element-1416">
                      <CrownIcon className="h-16 w-16 text-gray-400 mx-auto mb-4" data-id="element-1417" />
                      <h3 className="text-lg font-medium text-gray-900 mb-2" data-id="element-1418">
                        No Active License
                      </h3>
                      <p className="text-gray-600 mb-6" data-id="element-1419">
                        Purchase a license to start listing your properties.
                      </p>
                      <Link to="/license-plans" className="inline-block bg-blue-600 hover:bg-blue-700 text-white font-medium px-6 py-2 rounded-md transition duration-200" data-id="element-1420">
                        View License Plans
                      </Link>
                    </div>}
                </div>
              </div>}
            {activeTab === 'profile' && <div className="bg-white rounded-lg shadow-md p-6" data-id="element-1421">
                <h2 className="text-xl font-semibold text-gray-900 mb-6" data-id="element-1422">
                  {currentUser.companyName ? 'Agency Profile' : 'My Profile'}
                </h2>
                <div className="max-w-2xl mx-auto" data-id="element-1423">
                  <div className="mb-8" data-id="element-1424">
                    <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1425">
                      Company Logo
                    </label>
                    <div className="flex items-center space-x-4" data-id="element-1426">
                      <div className="h-24 w-24 rounded-lg bg-gray-100 flex items-center justify-center overflow-hidden" data-id="element-1427">
                        {companyLogo ? <img src={companyLogo} alt="Company logo" className="h-full w-full object-cover" data-id="element-1428" /> : <BuildingIcon className="h-12 w-12 text-gray-400" data-id="element-1429" />}
                      </div>
                      <label className="cursor-pointer bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-medium px-4 py-2 rounded-md transition duration-200" data-id="element-1430">
                        <UploadIcon className="h-4 w-4 inline mr-2" data-id="element-1431" />
                        Upload Logo
                        <input type="file" accept="image/*" onChange={handleLogoUpload} className="hidden" data-id="element-1432" />
                      </label>
                    </div>
                    <p className="mt-2 text-sm text-gray-500" data-id="element-1433">
                      Recommended: Square image, at least 200x200px
                    </p>
                  </div>
                  <form className="space-y-6" data-id="element-1434">
                    <div className="border-b border-gray-200 pb-6" data-id="element-1435">
                      <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1436">
                        Company Information
                      </h3>
                      <div className="space-y-4" data-id="element-1437">
                        <div data-id="element-1438">
                          <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1439">
                            Company Name
                          </label>
                          <input type="text" id="companyName" name="companyName" defaultValue={currentUser.companyName} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Your Real Estate Agency" data-id="element-1440" />
                        </div>
                        <div data-id="element-1441">
                          <label htmlFor="companyNIF" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1442">
                            Company NIF (Tax ID)
                          </label>
                          <input type="text" id="companyNIF" name="companyNIF" defaultValue={currentUser.companyNIF} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="123456789" data-id="element-1443" />
                          <p className="mt-1 text-sm text-gray-500" data-id="element-1444">
                            Your company tax identification number
                          </p>
                        </div>
                      </div>
                    </div>
                    <div data-id="element-1445">
                      <h3 className="text-lg font-medium text-gray-900 mb-4" data-id="element-1446">
                        Contact Information
                      </h3>
                      <div className="space-y-4" data-id="element-1447">
                        <div data-id="element-1448">
                          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1449">
                            Contact Person Name
                          </label>
                          <input type="text" id="name" name="name" defaultValue={currentUser.name} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-1450" />
                        </div>
                        <div data-id="element-1451">
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1452">
                            Email Address
                          </label>
                          <input type="email" id="email" name="email" defaultValue={currentUser.email} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-1453" />
                        </div>
                        <div data-id="element-1454">
                          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1" data-id="element-1455">
                            Phone Number
                          </label>
                          <input type="tel" id="phone" name="phone" defaultValue={currentUser.phone} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-1456" />
                        </div>
                      </div>
                    </div>
                    <div className="pt-4" data-id="element-1457">
                      <button type="submit" className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md transition duration-200" data-id="element-1458">
                        Update Profile
                      </button>
                    </div>
                  </form>
                </div>
              </div>}
          </div>
        </div>
      </div>
    </div>;
};
export default UserDashboardPage;